import { ProjectRole } from '@consigli/types';
import { FC, useCallback } from 'react';
import { HiOutlineShieldCheck, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi2';

import { ProjectAccess, servicePermissionOptions } from './types';

interface ProjectAccessListProps {
  projects: ProjectAccess[];
  onEdit: (project: ProjectAccess) => void;
  onRemove: (projectId: number) => void;
}

export const ProjectAccessList: FC<ProjectAccessListProps> = ({ projects, onEdit, onRemove }) => {
  const getPermissionLabel = useCallback((value: string) => {
    return servicePermissionOptions.find((opt) => opt.value === value)?.label ?? value;
  }, []);

  if (projects.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {projects.map((project) => (
        <div key={project.id} className="p-4 bg-muted/50 border rounded-lg relative group">
          {/* Project name and actions */}
          <div className="flex items-start justify-between">
            <div className="font-semibold text-foreground">{project.name}</div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => onEdit(project)}
                className="p-1.5 hover:bg-muted rounded-md text-muted-foreground hover:text-foreground"
                aria-label="Edit project access"
              >
                <HiOutlinePencil className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => onRemove(project.id)}
                className="p-1.5 hover:bg-muted rounded-md text-muted-foreground hover:text-destructive"
                aria-label="Remove project access"
              >
                <HiOutlineTrash className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Role/permissions info */}
          <div className="mt-2 text-sm">
            {project.role === ProjectRole.ADMIN ? (
              <div className="flex items-center gap-1.5 text-muted-foreground">
                <HiOutlineShieldCheck className="h-5 w-5" />
                <span className="font-medium">Project admin</span>
              </div>
            ) : (
              <div>
                <span className="font-medium text-muted-foreground">Service access: </span>
                <span className="text-foreground">
                  {project.permissions.length > 0
                    ? project.permissions.map((p) => getPermissionLabel(p)).join(', ')
                    : 'None'}
                </span>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
